/**
 * Facet 详情页面
 * 展示单个场景人格面的详细信息
 * 
 * 功能：
 * - 顶部：场景信息、状态、操作按钮
 * - 2 个 Tab：injection_pack 查看、Quality Gate 测试
 * 
 * 参考：API 文档 8.2 节、第 9 节
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import {
  ArrowLeft,
  Loader2,
  Play,
  Rocket,
  RefreshCw,
  AlertCircle,
  FileText,
  Shield,
  Mic,
  Volume2,
} from 'lucide-react'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'
import { useToast } from '@/shared/hooks/useToast'
import { FacetApiService, IdentityApiService } from '@/modules/identity/services'
import type { Voice } from '@/modules/identity/types'
import type { FacetDetail as FacetDetailType, SceneTag } from '@/modules/identity/types/facet'
import { FACET_STATUS_CONFIG, SCENE_TAG_CONFIG } from '@/modules/identity/types/facet'
import { InjectionPackViewer } from './tabs/InjectionPackViewer'
import { QualityGateTab } from './tabs/QualityGateTab'
import { FacetVoiceTab } from './tabs/FacetVoiceTab'
import { LearningProgressBar } from '@/modules/identity/components/LearningProgressBar'
import type { FacetVoiceBinding } from '@/modules/identity/types/facet'

export default function FacetDetail() {
  const { id: identityId, sceneId } = useParams<{ id: string; sceneId: string }>()
  const navigate = useNavigate()
  const { showSuccess, showError } = useToast()

  // 数据状态
  const [facet, setFacet] = useState<FacetDetailType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('injection')

  // 操作状态
  const [learning, setLearning] = useState(false)
  const [publishing, setPublishing] = useState(false)

  // v10.0 新增：发布对话框状态
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [publishVoiceId, setPublishVoiceId] = useState('')
  const [publishDescription, setPublishDescription] = useState('')
  const [availableVoices, setAvailableVoices] = useState<Voice[]>([])
  const [loadingVoices, setLoadingVoices] = useState(false)
  const [showProgressStream, setShowProgressStream] = useState(false)

  // v10.2 新增：音色绑定状态
  const [voiceBinding, setVoiceBinding] = useState<FacetVoiceBinding | null>(null)

  // ==================== 数据加载 ====================
  const loadFacet = useCallback(async () => {
    if (!identityId || !sceneId) return

    setLoading(true)
    setError(null)
    try {
      const response = await FacetApiService.getFacet(identityId, sceneId as SceneTag)
      setFacet(response.data)
      // v10.2: 设置绑定信息（从 facet 数据中获取，或单独加载）
      setVoiceBinding(response.data.voice_binding || null)
    } catch (err) {
      console.error('Failed to load facet:', err)
      setError('无法加载场景人格详情')
    } finally {
      setLoading(false)
    }
  }, [identityId, sceneId])

  // v10.2: 加载绑定信息
  const loadVoiceBinding = useCallback(async () => {
    if (!identityId || !sceneId) return

    try {
      const response = await IdentityApiService.getFacetVoiceBinding(identityId, sceneId)
      setVoiceBinding(response.data.voice_binding)
    } catch (err) {
      console.error('Failed to load voice binding:', err)
      // 可能是 404（未绑定），忽略错误
      setVoiceBinding(null)
    }
  }, [identityId, sceneId])

  useEffect(() => {
    loadFacet()
    loadVoiceBinding()  // v10.3: 同时加载绑定信息，确保显示正确
  }, [loadFacet, loadVoiceBinding])

  // 当进入学习状态时，启用 SSE 进度流
  useEffect(() => {
    if (facet?.status === 'learning') {
      setShowProgressStream(true)
    }
  }, [facet?.status])

  // ==================== 操作处理 ====================
  const handleBack = useCallback(() => {
    // 返回到身份详情页的「场景人格」Tab
    navigate(`/identities/${identityId}?tab=facet`)
  }, [navigate, identityId])

  const handleLearn = useCallback(async () => {
    if (!identityId || !sceneId) return

    setLearning(true)
    try {
      await FacetApiService.learnFacet(identityId, sceneId as SceneTag)
      showSuccess('学习任务已启动', '开始学习')
      setShowProgressStream(true) // 启用 SSE 实时进度
      loadFacet()
    } catch (err) {
      console.error('Failed to start learning:', err)
      showError('启动学习失败', '操作失败')
    } finally {
      setLearning(false)
    }
  }, [identityId, sceneId, showSuccess, showError, loadFacet])

  /**
   * v10.0: 打开发布对话框（需要选择音色）
   */
  const handlePublishClick = useCallback(async () => {
    if (!identityId) return
    
    setPublishVoiceId('')
    setPublishDescription('')
    setPublishDialogOpen(true)
    
    // 加载可用音色列表
    setLoadingVoices(true)
    try {
      const response = await IdentityApiService.listVoices(identityId)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const voices = (response.data as any)?.data || response.data || []
      // v10.1 更新：只显示已发布的音色（必须先发布才能绑定到场景）
      const publishedVoices = voices.filter((v: Voice) => v.status === 'published')
      setAvailableVoices(publishedVoices)
    } catch (err) {
      console.error('Failed to load voices:', err)
      setAvailableVoices([])
    } finally {
      setLoadingVoices(false)
    }
  }, [identityId])

  /**
   * v10.0: 确认发布 Facet（包含 voice_id）
   * 发布后自动创建 Identity 版本记录
   */
  const handleConfirmPublish = useCallback(async () => {
    if (!identityId || !sceneId) return
    
    if (!publishVoiceId) {
      showError('请选择场景音色', '验证失败')
      return
    }

    setPublishing(true)
    try {
      // 1. 发布 Facet
      const publishResult = await FacetApiService.publishFacet(identityId, sceneId as SceneTag, {
        voice_id: publishVoiceId,
        description: publishDescription || undefined,
      })
      const facetVersion = publishResult.data?.version || 'unknown'
      const sceneName = SCENE_TAG_CONFIG[sceneId as SceneTag]?.label || sceneId
      
      // 2. 自动创建 Identity 版本（记录此次发布）
      try {
        await IdentityApiService.publishVersion(identityId, {
          description: `发布场景「${sceneName}」(v${facetVersion})`,
        })
        showSuccess(`「${sceneName}」已发布，版本已记录`, '发布成功')
      } catch (versionErr) {
        // 版本创建失败不影响 Facet 发布
        console.warn('Failed to create identity version:', versionErr)
        showSuccess(`「${sceneName}」已发布`, '发布成功')
      }
      
      setPublishDialogOpen(false)
      loadFacet()
    } catch (err) {
      console.error('Failed to publish:', err)
      showError('发布失败', '操作失败')
    } finally {
      setPublishing(false)
    }
  }, [identityId, sceneId, publishVoiceId, publishDescription, showSuccess, showError, loadFacet])

  // 保存 injection_pack 编辑
  const handleSaveInjectionPack = useCallback(async (injectionPack: any) => {
    if (!identityId || !sceneId) return

    try {
      await FacetApiService.updateFacet(identityId, sceneId as SceneTag, {
        injection_pack: injectionPack,
      })
      showSuccess('人格配置已保存', '保存成功')
      loadFacet()
    } catch (err) {
      console.error('Failed to save injection pack:', err)
      showError('保存失败', '操作失败')
      throw err // 让 InjectionPackViewer 知道保存失败
    }
  }, [identityId, sceneId, showSuccess, showError, loadFacet])

  // ==================== 渲染 ====================
  if (loading && !facet) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-violet-600" />
          <p className="text-slate-500">加载中...</p>
        </div>
      </div>
    )
  }

  if (error || !facet) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
          <p className="text-slate-600 mb-4">{error || '数据不存在'}</p>
          <Button variant="outline" onClick={handleBack}>
            返回
          </Button>
        </div>
      </div>
    )
  }

  const sceneConfig = SCENE_TAG_CONFIG[facet.scene_tag]
  const statusConfig = FACET_STATUS_CONFIG[facet.status]
  const isLearning = facet.status === 'learning'
  const isDraft = facet.status === 'draft'
  const canLearn = (isDraft || facet.status === 'failed' || facet.status === 'not_started') && facet.corpus_count > 0
  const canPublish = isDraft && facet.injection_pack !== null

  return (
    <div className="min-h-screen bg-slate-50">
      {/* 顶部导航 */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* 左侧：返回 + 标题 */}
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                返回
              </Button>
              <div className="flex items-center gap-3">
                <span className="text-3xl">{sceneConfig?.icon || '📋'}</span>
                <div>
                  <h1 className="text-xl font-semibold text-slate-800">
                    {facet.scene_name || sceneConfig?.label}
                  </h1>
                  <p className="text-sm text-slate-500">
                    {facet.scene_description || sceneConfig?.description}
                  </p>
                </div>
              </div>
            </div>

            {/* 右侧：状态 + 操作 */}
            <div className="flex items-center gap-3">
              {/* 状态徽章 */}
              <Badge
                variant="secondary"
                className={cn(
                  'text-sm px-3 py-1',
                  statusConfig?.bgColor,
                  statusConfig?.color
                )}
              >
                {statusConfig?.icon} {statusConfig?.label}
              </Badge>

              {/* 版本 */}
              {facet.version && (
                <Badge variant="outline">v{facet.version}</Badge>
              )}

              {/* 刷新按钮 */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadFacet()}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>

              {/* 学习按钮 */}
              {canLearn && (
                <Button
                  size="sm"
                  onClick={handleLearn}
                  disabled={learning}
                >
                  {learning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  开始学习
                </Button>
              )}

              {/* 发布按钮 */}
              {canPublish && (
                <Button
                  size="sm"
                  onClick={handlePublishClick}
                  disabled={publishing}
                >
                  {publishing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Rocket className="h-4 w-4 mr-2" />
                  )}
                  发布
                </Button>
              )}
            </div>
          </div>

          {/* 学习进度条 - SSE 实时 */}
          {(isLearning || showProgressStream) && identityId && (
            <div className="mt-4 bg-slate-900 rounded-lg p-4">
              <LearningProgressBar
                identityId={identityId}
                sceneId={sceneId as SceneTag}
                onComplete={() => {
                  setShowProgressStream(false)
                  showSuccess('学习完成！', '成功')
                  loadFacet()
                }}
                onError={(err) => {
                  setShowProgressStream(false)
                  showError(err, '学习失败')
                  loadFacet()
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* 统计信息 */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center gap-6 text-sm text-slate-600">
          <span>📝 语料: <strong>{facet.corpus_count}</strong> 条</span>
          {facet.test_pass_rate !== null && (
            <span>
              🧪 测试: <strong className={cn(
                facet.test_pass_rate >= 0.9 ? 'text-emerald-600' :
                facet.test_pass_rate >= 0.7 ? 'text-amber-600' : 'text-red-600'
              )}>{Math.round(facet.test_pass_rate * 100)}%</strong>
            </span>
          )}
          {facet.learned_at && (
            <span>🕐 学习于: {new Date(facet.learned_at).toLocaleString('zh-CN')}</span>
          )}
        </div>
      </div>

      {/* Tab 内容 */}
      <div className="max-w-7xl mx-auto px-6 pb-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white shadow-sm border">
            <TabsTrigger value="injection" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Injection Pack
            </TabsTrigger>
            <TabsTrigger value="quality" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Quality Gate
            </TabsTrigger>
            <TabsTrigger value="voice" className="flex items-center gap-2">
              <Volume2 className="h-4 w-4" />
              音色
            </TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="injection" className="mt-0">
              {facet.injection_pack ? (
                <InjectionPackViewer
                  injectionPack={facet.injection_pack}
                  featureStore={facet.feature_store}
                  editable={!isLearning}
                  onSave={handleSaveInjectionPack}
                />
              ) : (
                <Card>
                  <CardContent className="py-12 text-center text-slate-400">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>暂无 Injection Pack</p>
                    <p className="text-sm mt-2">请先上传语料并开始学习</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="quality" className="mt-0">
              <QualityGateTab
                identityId={identityId!}
                sceneId={sceneId as SceneTag}
                canPublish={canPublish}
                onPublish={handlePublishClick}
              />
            </TabsContent>

            <TabsContent value="voice" className="mt-0">
              <FacetVoiceTab
                identityId={identityId!}
                sceneId={sceneId!}
                sceneName={facet?.scene_name || sceneConfig?.label || sceneId!}
                voiceBinding={voiceBinding}
                onBindingChange={() => {
                  loadFacet()
                  loadVoiceBinding()
                }}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* v10.0 新增：发布对话框（需要选择音色）*/}
      <Dialog open={publishDialogOpen} onOpenChange={(open) => {
        setPublishDialogOpen(open)
        if (!open) {
          setPublishVoiceId('')
          setPublishDescription('')
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-violet-500" />
              发布场景「{facet?.scene_name || sceneId}」
            </DialogTitle>
            <DialogDescription>
              发布后该场景人格将可用于 Agent Flow 中的身份模拟。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* 音色选择（必填）*/}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Mic className="h-4 w-4" />
                场景音色 <span className="text-red-500">*</span>
              </Label>
              {loadingVoices ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  加载音色列表...
                </div>
              ) : availableVoices.length === 0 ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    暂无已发布的音色
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    请先在「声音克隆」Tab 完成音色训练并发布后再绑定到场景。
                  </p>
                </div>
              ) : (
                <Select value={publishVoiceId} onValueChange={setPublishVoiceId}>
                  <SelectTrigger>
                    <SelectValue placeholder="选择该场景使用的音色" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableVoices.map((voice) => (
                      <SelectItem key={voice.id} value={voice.id}>
                        <div className="flex items-center gap-2">
                          <Mic className="h-3 w-3 text-slate-400" />
                          <span>{voice.name}</span>
                          {voice.is_default && (
                            <Badge variant="secondary" className="text-xs">默认</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-slate-400">
                选择该场景在 Agent Flow 运行时使用的声音。
              </p>
            </div>

            {/* 版本说明（可选）*/}
            <div className="space-y-2">
              <Label>版本说明（可选）</Label>
              <Textarea
                value={publishDescription}
                onChange={(e) => setPublishDescription(e.target.value)}
                placeholder="描述本次发布的更新内容..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPublishDialogOpen(false)}
              disabled={publishing}
            >
              取消
            </Button>
            <Button
              onClick={handleConfirmPublish}
              disabled={!publishVoiceId || publishing || availableVoices.length === 0}
            >
              {publishing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  发布中...
                </>
              ) : (
                '确认发布'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
